import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  searchTeamMembers,
  addTeamMember,
  setSearchBy,
  setSearchNuid,
  setSearchFirstName,
  setSearchLastName,
  setSearchHasSearched,
  setSearchPage,
  setSearchSorting,
  resetSearch,
} from "../../../store/slices/memberSlice";
import Loader from "../../../components/common/Loader";
import CardTable from "../../../components/common/CardTable";
import NoResultsFound from "../../../components/common/NoResultsFound";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import Table from "../../../components/common/Table/Table";
import Pagination from "../../../components/common/Table/Pagination";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import { addMembersColumn } from "./membersColumns";
import { SimpleTableTabProps } from "./membersTypes";

const AddMembersTab: React.FC<SimpleTableTabProps> = (props) => {
  const dispatch = useAppDispatch();

  const addMemberLoading = useAppSelector(
    (state) => state.membersSlice.addMemberLoading,
  );
  const addMemberError = useAppSelector(
    (state) => state.membersSlice.addMemberError,
  );

  const searchBy = useAppSelector(
    (state) => state.membersSlice.searchBy ?? "name",
  );
  const nuid = useAppSelector(
    (state) => state.membersSlice.searchNuid ?? "",
  );
  const firstName = useAppSelector(
    (state) => state.membersSlice.searchFirstName ?? "",
  );
  const lastName = useAppSelector(
    (state) => state.membersSlice.searchLastName ?? "",
  );
  const hasSearched = useAppSelector(
    (state) => state.membersSlice.searchHasSearched ?? false,
  );
  const searchPage = useAppSelector(
    (state) => state.membersSlice.searchPage ?? 1,
  );
  const searchSortColumn = useAppSelector(
    (state) => state.membersSlice.searchSortColumn ?? "lastName",
  );
  const searchSortDirection = useAppSelector(
    (state) => state.membersSlice.searchSortDirection ?? "asc",
  );

  const [nuidError, setNuidError] = useState("");

  const searchResults = useAppSelector(
    (state) => state.membersSlice.searchResults ?? [],
  );
  const searchTotal = useAppSelector(
    (state) => state.membersSlice.searchTotal ?? 0,
  );
  const searchLoading = useAppSelector(
    (state) => state.membersSlice.searchLoading,
  );
  const searchError = useAppSelector((state) => state.membersSlice.searchError);
  const teamDetails = useAppSelector((state) => state.membersSlice.teamDetails);
  let defaultMembership: "Y" | "N" | undefined;
  if (teamDetails?.defaultMembership === "Yes") {
    defaultMembership = "Y";
  } else if (teamDetails?.defaultMembership === "No") {
    defaultMembership = "N";
  }

  const teamId = props.teamId ?? "";
  const ipp = props.itemsPerPage ?? 10;

  // Prevents the auto-refresh from firing more than once per mount.
  const didAutoRefreshRef = useRef(false);

  const runSearch = (
    page = 1,
    sortCol = searchSortColumn,
    sortDir = searchSortDirection,
  ) => {
    if (searchBy === "nuid" && !(nuid ?? "").trim()) {
      setNuidError("NUID is required.");
      return;
    }
    setNuidError("");
    dispatch(setSearchHasSearched(true));
    dispatch(setSearchPage(page));

    const apiSortColumn = sortCol === "lastName" ? "LAST_NAME" : "FIRST_NAME";
    const apiSortOrder = (sortDir ?? "asc").toUpperCase() as "ASC" | "DESC";

    const params: any = {
      searchBy,
      teamId,
      sortColumn: apiSortColumn,
      sortOrder: apiSortOrder,
      page,
      per_page: ipp,
      ...(defaultMembership ? { defaultMembership } : {}),
    };
    if (searchBy === "nuid") {
      params.nuid = (nuid ?? "").trim();
    } else {
      if ((firstName ?? "").trim()) params.firstName = (firstName ?? "").trim();
      if ((lastName ?? "").trim()) params.lastName = (lastName ?? "").trim();
    }
    dispatch(searchTeamMembers(params));
  };

  // When the user navigates back to this tab after adding a member, re-run
  // the previous search automatically so the results are up to date and the
  // just-added member no longer appears in the list.
  useEffect(() => {
    if (hasSearched && !didAutoRefreshRef.current) {
      didAutoRefreshRef.current = true;
      runSearch(searchPage, searchSortColumn, searchSortDirection);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(1);
  };

  const handleReset = () => {
    dispatch(resetSearch());
    setNuidError("");
  };

  const handleSearchByChange = (option: DropdownOption) => {
    dispatch(setSearchBy(option.value as "name" | "nuid"));
    setNuidError("");
  };

  const handleSearchSort = (columnKey: string, direction: "ASC" | "DESC") => {
    const lowerDir = direction.toLowerCase() as "asc" | "desc";
    dispatch(
      setSearchSorting({ sortColumn: columnKey, sortDirection: lowerDir }),
    );
    runSearch(1, columnKey, lowerDir);
  };

  const handleAddMember = async (memberNuid: string) => {
    const record = searchResults.find((r: any) => r.nuid === memberNuid);
    if (!record) return;
    const result = await dispatch(
      addTeamMember({
        teamId: props.teamId ?? "",
        deptPk: record.deptpk,
        nuid: record.nuid,
      }),
    );
    if (!result.type.endsWith("/rejected") && props.onSwitchToManualTab) {
      props.onSwitchToManualTab();
    }
  };

  const searchDisplayRows = (searchResults ?? []).map(
    (r: any, index: number) => ({
      ...r,
      rowKey: r.nuid ?? `search-row-${index}`,
      action: (
        <button
          className="button flex items-center group hover:text-white! focus:text-white! "
          data-analytics-type="button"
          data-analytics-click="Default"
          onClick={() => handleAddMember(r.nuid)}
        >
          <span>Add</span>
        </button>
      ),
    }),
  );

  const renderSearchResults = () => {
    if (searchLoading) {
      return (
        <div className="flex justify-center items-center min-h-[200px]">
          <Loader />
        </div>
      );
    }
    if (searchError) {
      return (
        <CommonErrorBanner
          heading="Search Error"
          messageInfo="The server encountered an issue while searching. Please try again later."
        />
      );
    }
    if (searchDisplayRows.length === 0) {
      return (
        <NoResultsFound
          title="No members found"
          message="No members match your search criteria."
        />
      );
    }
    return (
      <>
        {addMemberError && (
          <CommonErrorBanner
            heading="Add Member Error"
            messageInfo={addMemberError}
          />
        )}
        <div className="relative">
          {addMemberLoading && (
            <div className="absolute inset-0 flex justify-center items-center bg-white/60 z-10">
              <Loader />
            </div>
          )}
          <Table
            columns={addMembersColumn}
            rows={searchDisplayRows}
            onSort={handleSearchSort}
            sortColumn={searchSortColumn}
            sortDirection={searchSortDirection.toUpperCase() as "ASC" | "DESC"}
          />
        </div>
        <div className="mt-4! left-0 mobile:relative mobile:left-[30px]">
          <Pagination
            totalItems={searchTotal}
            itemsPerPage={ipp}
            currentPage={searchPage}
            onPageChange={(p: number) => runSearch(p)}
          />
        </div>
      </>
    );
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-[32px] flex-wrap mt-[36px]! w-full"
      >
        <div className="grid grid-cols-[max-content_max-content_1fr_1fr] gap-8 items-start">
          <div
            className="ds-form"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <Dropdown
              label="Search by"
              selectedValue={{
                id: searchBy,
                label: searchBy === "nuid" ? "NUID" : "Name",
                value: searchBy,
              }}
              options={[
                { id: "name", label: "Name", value: "name" },
                { id: "nuid", label: "NUID", value: "nuid" },
              ]}
              onSelect={handleSearchByChange}
              buttonClassName="min-w-[12rem]"
              menuClassName="min-w-[12rem]"
              placeholder="Search by"
            />
          </div>

          {searchBy === "nuid" ? (
            <div
              className="ds-form w-full"
              data-ds-theme="vessel"
              data-ds-variant="input"
              data-ds-version="1"
            >
              <label htmlFor="nuid">NUID</label>
              <div className="w-full relative">
                <input
                  autoComplete="off"
                  className={`w-full min-w-0 pr-10${nuidError ? " border-red-800!" : ""}`}
                  id="nuid"
                  name="nuid"
                  type="text"
                  value={nuid}
                  onChange={(e) => {
                    dispatch(setSearchNuid(e.target.value));
                    if (e.target.value.trim()) setNuidError("");
                  }}
                  onFocus={() => setNuidError("")}
                />
              </div>
              {nuidError && (
                <div className="inline-flex items-center gap-2 mt-1">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    width="14"
                    height="14"
                  />
                  <span className="text-red-600 text-sm">{nuidError}</span>
                </div>
              )}
            </div>
          ) : (
            <>
              <div
                className="ds-form w-full"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="firstName">First Name</label>
                <div className="w-full relative">
                  <input
                    autoComplete="on"
                    className="w-full min-w-0 pr-10"
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) =>
                      dispatch(setSearchFirstName(e.target.value))
                    }
                  />
                </div>
              </div>
              <div
                className="ds-form w-full"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="lastName">Last Name</label>
                <div className="w-full relative">
                  <input
                    autoComplete="on"
                    className="w-full min-w-0 block pr-10"
                    id="lastName"
                    name="empLastName"
                    type="text"
                    value={lastName}
                    onChange={(e) =>
                      dispatch(setSearchLastName(e.target.value))
                    }
                  />
                </div>
              </div>
            </>
          )}

          <div className={searchBy === "nuid" ? "col-span-2" : ""}></div>

          <button
            type="button"
            onClick={handleReset}
            className="button -light m-0 block w-full min-[992px]:w-auto self-stretch flex items-center justify-center box-border max-w-none h-10"
          >
            <span className="flex items-center justify-center w-full">
              Reset Search
            </span>
          </button>

          <button
            type="submit"
            className="button -primary -light block w-full min-[992px]:w-auto h-10 m-0 self-stretch flex items-center justify-center box-border max-w-none ml--22p"
          >
            <span className="flex items-center justify-center w-full">
              Search
            </span>
          </button>
        </div>
      </form>

      {hasSearched && (
        <div className="mt-6!">
          <CardTable>{renderSearchResults()}</CardTable>
        </div>
      )}
    </div>
  );
};

export default AddMembersTab;
